import React, { useState } from 'react';
import { Plus, X, Loader2, GripVertical, Eye, Settings2, Wand2 } from 'lucide-react';
import { motion } from 'motion/react';
import { moduleApi } from '../../lib/api';
import { ApercuSimulateur } from '../ui/ApercuSimulateur';

// Objets du parcours guidé du simulateur (2026-09-09, demande d'Emeni pour Shampouine le !).
//
// Pourquoi un niveau de plus, et pas un redécoupage des catégories : la catégorie ("Assise") porte les
// seuils d'éligibilité des formules — « CONFORT à partir de 80 en Assise ». La redécouper en Canapé /
// Chaise / Fauteuil casserait ces seuils déjà configurés. L'objet est donc un niveau d'affichage posé
// par-dessus : c'est ce que le visiteur nomme ("un canapé"), la catégorie reste ce que le commerce
// compte. Un objet appartient à une catégorie, et regroupe les prestations qui répondent à sa question.
export interface PrestationSubcategory {
    id: string;
    category_id: string;
    name: string;
    // « Combien de places ? », « Quelle taille ? » — la question posée après le choix de l'objet. Vide,
    // le simulateur retombe sur un libellé générique.
    question_label: string;
    // Petite précision sous le nom, sur la carte : « Droit, d'angle, convertible ».
    hint: string;
    // Le visiteur peut-il cocher plusieurs variantes d'un coup (un 2 places ET un 3 places) ?
    multi_choix: boolean;
    position: number;
    // Public de l'objet : « Banquette » n'a rien a faire chez un particulier, « Matelas » rien a faire
    // dans un catalogue de restaurant. Le public se pose ICI, jamais sur la categorie — la categorie
    // (« Assise ») reste commune, c'est elle qui porte les seuils de formule, lesquels englobent
    // justement plusieurs objets : canape, chaise, fauteuil.
    client_type_scope: 'TOUS' | 'PROFESSIONNEL' | 'PRIVÉ';
}

interface CategoryLite { id: string; name: string }

const INPUT = 'w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500 transition-colors';
const LABEL = 'text-[10px] font-bold text-content-muted uppercase tracking-widest';

function ObjetRow({
    objet,
    categories,
    nbPrestations,
    onChanged,
}: {
    objet: PrestationSubcategory;
    categories: CategoryLite[];
    nbPrestations: number;
    onChanged: () => void;
}) {
    const [draft, setDraft] = useState<PrestationSubcategory>(objet);
    const [saving, setSaving] = useState(false);

    // Enregistrement à la sortie du champ : pas de bouton « Enregistrer » par objet, qui multiplierait
    // les clics sur un écran qui en compte déjà beaucoup.
    const save = async (patch: Partial<PrestationSubcategory>) => {
        const next = { ...draft, ...patch };
        setDraft(next);
        setSaving(true);
        try {
            await moduleApi.updatePrestationSubcategory(objet.id, {
                category_id: next.category_id,
                name: next.name,
                question_label: next.question_label,
                hint: next.hint,
                multi_choix: next.multi_choix,
                client_type_scope: next.client_type_scope,
            });
            onChanged();
        } catch (e) { /* l'écran reste sur la valeur saisie ; le rechargement corrigera */ }
        finally { setSaving(false); }
    };

    const remove = async () => {
        try { await moduleApi.deletePrestationSubcategory(objet.id); onChanged(); } catch (e) {}
    };

    return (
        <div className="p-2.5 rounded-xl bg-surface border border-border space-y-2">
            <div className="flex items-center gap-2">
                <GripVertical size={13} className="text-content-muted shrink-0" />
                <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    onBlur={() => draft.name.trim() && draft.name !== objet.name && save({})}
                    className="flex-1 bg-transparent border-0 px-0 py-0 text-sm font-bold text-content outline-none"
                />
                <span className={`text-[10px] shrink-0 ${nbPrestations === 0 ? 'text-amber-600 dark:text-amber-400 font-bold' : 'text-content-muted'}`}>
                    {nbPrestations === 0 ? 'aucune prestation' : `${nbPrestations} prestation${nbPrestations > 1 ? 's' : ''}`}
                </span>
                {saving && <Loader2 size={12} className="animate-spin text-content-muted shrink-0" />}
                <button type="button" onClick={remove} className="text-content-muted hover:text-red-400 shrink-0"><X size={13} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="space-y-1">
                    <label className={LABEL}>Compte dans la formule</label>
                    <select value={draft.category_id} onChange={(e) => save({ category_id: e.target.value })} className={INPUT}>
                        <option value="">Aucune</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="space-y-1">
                    <label className={LABEL}>Question suivante</label>
                    <input
                        type="text"
                        value={draft.question_label}
                        placeholder="Combien de places ?"
                        onChange={(e) => setDraft((d) => ({ ...d, question_label: e.target.value }))}
                        onBlur={() => draft.question_label !== objet.question_label && save({})}
                        className={INPUT}
                    />
                </div>
                <div className="space-y-1">
                    <label className={LABEL}>Proposée à</label>
                    <select value={draft.client_type_scope || 'TOUS'}
                        onChange={(e) => save({ client_type_scope: e.target.value as PrestationSubcategory['client_type_scope'] })}
                        className={INPUT}>
                        <option value="TOUS">Tous les clients</option>
                        <option value="PROFESSIONNEL">Professionnels</option>
                        <option value="PRIVÉ">Particuliers</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <label className={LABEL}>Précision sous le nom</label>
                    <input
                        type="text"
                        value={draft.hint}
                        placeholder="Droit, d'angle, convertible"
                        onChange={(e) => setDraft((d) => ({ ...d, hint: e.target.value }))}
                        onBlur={() => draft.hint !== objet.hint && save({})}
                        className={INPUT}
                    />
                </div>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-content-muted cursor-pointer">
                <input type="checkbox" checked={draft.multi_choix} onChange={(e) => save({ multi_choix: e.target.checked })}
                    className="accent-brand-600" />
                Plusieurs réponses possibles à cette question suivante
            </label>
            {nbPrestations === 0 && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                    Cette réponse n'apparaîtra pas tant qu'aucune prestation ne lui est rattachée. Le rattachement se
                    fait sur la fiche de chaque prestation du catalogue, champ « Objet ».
                </p>
            )}
        </div>
    );
}

function SimulateurObjetsManager({
    subcategories,
    categories,
    countByObjet,
    audience,
    catalogueModifie,
    onChanged,
}: {
    subcategories: PrestationSubcategory[];
    categories: CategoryLite[];
    countByObjet: Record<string, number>;
    audience: 'TOUS' | 'PROFESSIONNEL' | 'PRIVÉ';
    catalogueModifie: boolean;
    onChanged: () => void;
}) {
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // La première question du parcours vit avec ses réponses (2026-09-09) : Emeni ne voyait pas le lien
    // entre « Que souhaitez-vous faire nettoyer ? », réglée dans Paramètres, et les objets créés ici —
    // qui en sont pourtant les réponses. Les deux sont donc sur le même écran, dans l'ordre où on les lit.
    const [question, setQuestion] = useState('');
    const [questionChargee, setQuestionChargee] = useState(false);
    const [multiObjets, setMultiObjets] = useState(false);

    // UN PARCOURS PAR PUBLIC (2026-09-21) : la question d'accueil et le « plusieurs réponses possibles »
    // appartiennent au parcours, donc au public. Les clés historiques restent celles du parcours
    // particulier — un établissement déjà configuré ne voit rien bouger.
    const estPro = audience === 'PROFESSIONNEL';
    const cleQuestion = estPro ? 'simulateur_question_pro' : 'simulateur_question';
    const cleMulti = estPro ? 'simulateur_multi_objets_pro' : 'simulateur_multi_objets';

    React.useEffect(() => {
        setQuestionChargee(false);
        moduleApi.getPrestationPlanningSettings().then((s: any) => {
            setQuestion(s?.[cleQuestion] || '');
            setMultiObjets(!!s?.[cleMulti]);
        }).catch(() => {}).finally(() => setQuestionChargee(true));
    }, [cleQuestion, cleMulti]);

    const enregistrerQuestion = async (patch: Record<string, unknown>) => {
        try { await moduleApi.savePrestationPlanningSettings(patch); onChanged(); } catch (e) {}
    };

    const add = async () => {
        const name = newName.trim();
        if (!name) return;
        setSaving(true); setError('');
        try {
            const res: any = await moduleApi.createPrestationSubcategory({ name, client_type_scope: audience });
            if (res?.error) { setError(res.error); return; }
            setNewName(''); onChanged();
        } catch (e: any) { setError(e?.message || 'Erreur'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-3">
            <div className="p-3 rounded-xl bg-surface-soft/60 border border-border space-y-2">
                <p className={LABEL}>Première question — parcours {estPro ? 'professionnel' : 'particulier'}</p>
                <input
                    type="text"
                    value={question}
                    placeholder="Que souhaitez-vous faire nettoyer ?"
                    disabled={!questionChargee}
                    onChange={(e) => setQuestion(e.target.value)}
                    onBlur={() => enregistrerQuestion({ [cleQuestion]: question })}
                    className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm font-semibold text-content outline-none focus:border-brand-500 transition-colors"
                />
                <label className="flex items-center gap-2 text-[11px] text-content-muted cursor-pointer">
                    <input type="checkbox" checked={multiObjets}
                        onChange={(e) => { setMultiObjets(e.target.checked); enregistrerQuestion({ [cleMulti]: e.target.checked }); }}
                        className="accent-brand-600" />
                    Plusieurs réponses possibles — le visiteur coche ce qu'il veut, on les enchaîne ensuite
                </label>
            </div>

        <div className="p-3 rounded-xl bg-surface-soft/60 border border-border space-y-2">
            <p className={LABEL}>Ses réponses</p>
            <p className="text-[11px] text-content-muted">
                Ce que le visiteur voit sous la question ci-dessus : « Canapé », « Lit », « Chaise »… Chaque réponse
                pose ensuite sa propre question — « Combien de places ? » — et c'est là qu'apparaissent les
                prestations de votre catalogue que vous lui rattachez. Sans réponse créée ici, le simulateur affiche
                la liste complète comme aujourd'hui.
            </p>
            <p className="text-[11px] text-content-muted bg-surface border border-border rounded-lg px-2.5 py-2">
                <b className="text-content">Vos catégories ne bougent pas.</b> « Assise » reste telle quelle et continue
                de porter vos seuils de formule — 80 en Assise, c'est toujours vos chaises, vos fauteuils et vos canapés
                réunis. Vous n'avez rien à supprimer : chaque réponse ci-dessous dit simplement dans quelle catégorie
                elle compte.
            </p>
            {catalogueModifie && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-2">
                    <b>Votre catalogue a des modifications non enregistrées.</b> Les réponses ci-dessous s'enregistrent
                    toutes seules, mais le rattachement d'une prestation à une réponse, lui, part avec le catalogue :
                    fermez et cliquez sur « Enregistrer » pour le voir dans l'aperçu.
                </p>
            )}
            {error && <p className="text-[11px] text-red-500">{error}</p>}
            {subcategories.length > 0 && (
                <div className="space-y-2">
                    {subcategories.map((o) => (
                        <ObjetRow key={o.id} objet={o} categories={categories} nbPrestations={countByObjet[o.id] || 0} onChanged={onChanged} />
                    ))}
                </div>
            )}
            <div className="flex items-center gap-2">
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
                    placeholder="Ex: Canapé, Lit, Chaise..." className="flex-1 bg-surface border border-border rounded-lg px-2.5 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                <button type="button" onClick={add} disabled={saving || !newName.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold shrink-0 disabled:opacity-40 disabled:bg-surface disabled:text-content-muted disabled:border disabled:border-border">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Ajouter une réponse
                </button>
            </div>
        </div>
        </div>
    );
}

// Le parcours guidé se règle ici, dans une modale, avec l'aperçu à côté (demande d'Emeni, 2026-09-09) :
// créer un objet et voir immédiatement ce que ça donne pour le visiteur, au lieu de configurer d'un côté
// et d'aller vérifier de l'autre. L'aperçu charge le VRAI widget sur les VRAIES prestations du catalogue,
// en forçant le mode guidé — l'établissement n'a donc pas à basculer son site pour se rendre compte.
export function SimulateurParcoursSettings({
    subcategories,
    categories,
    countByObjet,
    audience,
    catalogueModifie,
    onChanged,
}: {
    subcategories: PrestationSubcategory[];
    categories: CategoryLite[];
    countByObjet: Record<string, number>;
    audience: 'TOUS' | 'PROFESSIONNEL' | 'PRIVÉ';
    catalogueModifie: boolean;
    onChanged: () => void;
}) {
    const [ouvert, setOuvert] = useState(false);
    const [cleApercu, setCleApercu] = useState(0);
    const [modeSite, setModeSite] = useState<'liste' | 'guide' | null>(null);
    // Quand le widget n'est pas figé sur un public, l'établissement a DEUX parcours à écrire : celui des
    // particuliers et celui des professionnels. Cette bascule dit lequel on est en train de configurer —
    // elle change la question éditée, les objets affichés, et ce que montre l'aperçu.
    const [parcours, setParcours] = useState<'PRIVÉ' | 'PROFESSIONNEL'>(audience === 'PROFESSIONNEL' ? 'PROFESSIONNEL' : 'PRIVÉ');
    React.useEffect(() => { if (audience !== 'TOUS') setParcours(audience); }, [audience]);
    const parcoursLibre = audience === 'TOUS';
    // Le parent filtre déjà la liste quand le widget vise un public ; quand il ne vise personne, c'est la
    // bascule ci-dessus qui décide de ce qu'on voit — sinon les objets des deux publics se mélangeraient.
    const objetsDuParcours = parcoursLibre
        ? subcategories.filter((sc) => {
            const scope = (sc as any).client_type_scope || 'TOUS';
            return scope === 'TOUS' || scope === parcours;
        })
        : subcategories;

    React.useEffect(() => {
        if (!ouvert) return;
        moduleApi.getPrestationPlanningSettings()
            .then((s: any) => setModeSite(s?.simulateur_mode === 'guide' ? 'guide' : 'liste'))
            .catch(() => setModeSite(null));
    }, [ouvert]);

    // Chaque changement d'objet recharge l'aperçu : le widget lit sa configuration au chargement, il n'a
    // pas d'autre moyen de se rafraîchir.
    const rafraichir = () => { onChanged(); setCleApercu((k) => k + 1); };

    const nbRattachees = Object.values(countByObjet).reduce((a, b) => a + b, 0);

    return (
        <>
            <div className="p-3 rounded-xl bg-surface-soft/60 border border-border space-y-2">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                        <Wand2 size={17} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-content">Parcours guidé du simulateur</p>
                        <p className="text-[11px] text-content-muted mt-0.5">
                            Au lieu d'afficher tout votre catalogue, le simulateur demande d'abord l'objet à nettoyer
                            — « Canapé », « Matelas », « Tapis » — puis sa variante. Vos prestations restent les
                            réponses : rien à ressaisir.
                        </p>
                    </div>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] text-content-muted">
                        {subcategories.length === 0
                            ? 'Aucun objet créé — le simulateur affiche la liste complète.'
                            : `${subcategories.length} objet${subcategories.length > 1 ? 's' : ''}, ${nbRattachees} prestation${nbRattachees > 1 ? 's' : ''} rattachée${nbRattachees > 1 ? 's' : ''}.`}
                    </span>
                    <button type="button" onClick={() => setOuvert(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface border border-border hover:border-brand-500 text-content text-xs font-bold transition-all shrink-0">
                        <Settings2 size={13} /> Configurer le parcours
                    </button>
                </div>
            </div>

            {ouvert && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="w-full max-w-5xl bg-surface border border-border rounded-3xl max-h-[92vh] shadow-2xl flex flex-col overflow-hidden"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                                    <Wand2 size={19} />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-base font-bold text-content truncate">
                                        Parcours {parcours === 'PROFESSIONNEL' ? 'professionnel' : 'particulier'}
                                    </h3>
                                    <p className="text-[11px] text-content-muted">Vos objets à gauche, ce que verra votre visiteur à droite.</p>
                                </div>
                            </div>
                            <button onClick={() => setOuvert(false)} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all shrink-0">
                                <X size={19} />
                            </button>
                        </div>

                        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] overflow-hidden min-h-0">
                            <div className="p-5 overflow-y-auto space-y-3">
                                {parcoursLibre && (
                                    <div className="p-3 rounded-xl bg-surface-soft/60 border border-border space-y-1.5">
                                        <p className={LABEL}>Quel parcours configurez-vous ?</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {([['PRIVÉ', 'Particuliers'], ['PROFESSIONNEL', 'Professionnels']] as const).map(([v, libelle]) => (
                                                <button key={v} type="button" onClick={() => { setParcours(v); setCleApercu((k) => k + 1); }}
                                                    className={`rounded-lg border px-2 py-2 text-xs font-bold transition-all ${parcours === v
                                                        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                                                        : 'border-border text-content-muted hover:text-content hover:border-content-muted'}`}>
                                                    {libelle}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-content-muted">
                                            Chaque public a sa propre question d'accueil et ses propres objets. Un visiteur ne voit jamais
                                            le parcours de l'autre.
                                        </p>
                                    </div>
                                )}
                                <SimulateurObjetsManager
                                    subcategories={objetsDuParcours}
                                    categories={categories}
                                    countByObjet={countByObjet}
                                    audience={parcours}
                                    catalogueModifie={catalogueModifie}
                                    onChanged={rafraichir}
                                />
                            </div>
                            <div className="border-l border-border bg-surface-soft/40 p-4 flex flex-col min-h-0">
                                <div className="flex items-center gap-2 mb-2 shrink-0">
                                    <Eye size={13} className="text-content-muted" />
                                    <p className={LABEL}>Aperçu sur vos vraies prestations</p>
                                </div>
                                <div className="flex-1 rounded-2xl border border-border bg-white overflow-hidden min-h-[360px]">
                                    <ApercuSimulateur
                                        cle={cleApercu}
                                        forceMode="guide"
                                        audience={parcours === 'PROFESSIONNEL' ? 'professionnel' : 'particulier'}
                                    />
                                </div>
                                <p className="text-[11px] text-content-muted mt-2 shrink-0">
                                    {modeSite === 'liste'
                                        ? 'Aperçu du parcours guidé. Sur votre site, le simulateur affiche encore la liste complète — cela se bascule dans Paramètres.'
                                        : 'Visible de vous seul tant que le simulateur n\'est pas activé.'}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 p-5 border-t border-border shrink-0">
                            <button onClick={() => setOuvert(false)}
                                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
                                Fermer
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </>
    );
}
